/**
 * Holding, sending back and rejecting a payment run.
 *
 * Both approval screens were approve-or-nothing. An approver who spotted a
 * problem — wrong bank details, a DD document that did not look right, a price
 * that had changed — had no way to record it. The only options were to approve
 * anyway or to walk away, and the run then sat in the queue indefinitely with
 * nothing on the record to say why.
 *
 * Every action here writes a reason and an actor, because the reason is the
 * whole point: a held run with no explanation is just a stuck one.
 */

import { supabase } from '../lib/supabase'

export type HoldStage = 'first_approval' | 'second_release'

export interface HeldRun {
  id: string
  dealId: string
  invoiceLabel: string
  vehicle: string
  sellerName: string
  totalAmount: number
  status: string
  holdReason: string
  heldAtStage: HoldStage | null
  heldAt: string | null
  heldByName: string
  rejectedReason: string
  rejectedAt: string | null
  createdAt: string
}

const vehicleLabel = (d: any) =>
  [d?.autograb_year ?? d?.vehicle_year, d?.autograb_make || d?.vehicle_make, d?.autograb_model || d?.vehicle_model]
    .filter(Boolean)
    .join(' ')
    .trim() || 'Vehicle details unavailable'

/** Runs that are parked — held by an approver, or rejected outright. */
export async function loadHeldRuns(): Promise<HeldRun[]> {
  const [runs, deals, profiles] = await Promise.all([
    supabase
      .from('payment_runs')
      .select('*')
      .in('status', ['on_hold', 'rejected'])
      .order('created_at', { ascending: false }),
    supabase.from('deals').select('*').is('deleted_at', null),
    supabase.from('user_profiles').select('id, full_name'),
  ])
  for (const r of [runs, deals, profiles]) if (r.error) throw r.error

  // Joined in code rather than with an embedded select — an unverifiable embed
  // poisons the row type and has broken this build before.
  const dealById = new Map((deals.data || []).map((d: any) => [d.id, d]))
  const nameById = new Map((profiles.data || []).map((p: any) => [p.id, p.full_name]))

  return (runs.data || []).map((r: any): HeldRun => {
    const deal = dealById.get(r.deal_id)
    return {
      id: r.id,
      dealId: r.deal_id,
      invoiceLabel: String(r.id).slice(0, 8).toUpperCase(),
      vehicle: vehicleLabel(deal),
      sellerName: deal?.seller_name || '—',
      totalAmount: Number(r.total_amount) || 0,
      status: r.status,
      holdReason: r.hold_reason || '',
      heldAtStage: (r.held_at_stage as HoldStage) || null,
      heldAt: r.held_at || null,
      heldByName: nameById.get(r.held_by_user_id) || '—',
      rejectedReason: r.rejected_reason || '',
      rejectedAt: r.rejected_at || null,
      createdAt: r.created_at,
    }
  })
}

export async function holdRun(runId: string, stage: HoldStage, reason: string, userId: string): Promise<void> {
  if (!reason.trim()) throw new Error('A reason is required to hold a payment run.')
  const { error } = await supabase
    .from('payment_runs')
    .update({
      status: 'on_hold',
      hold_reason: reason.trim(),
      held_by_user_id: userId,
      held_at: new Date().toISOString(),
      held_at_stage: stage,
      updated_at: new Date().toISOString(),
    })
    .eq('id', runId)
  if (error) throw error
}

/**
 * Send a run back to the first approver.
 *
 * This clears the first approval as well as the second, so the run genuinely
 * starts again — leaving `winnie_review_complete` set would mean the six DD
 * checks were never re-done, which is the whole reason for sending it back.
 */
export async function sendBackToFirstApproval(runId: string, reason: string, userId: string): Promise<void> {
  if (!reason.trim()) throw new Error('Say what needs re-checking before sending it back.')
  const { error } = await supabase
    .from('payment_runs')
    .update({
      status: 'pending_winnie',
      winnie_review_complete: false,
      winnie_reviewed_at: null,
      winnie_dd_check_1_identity: false,
      winnie_dd_check_2_documents: false,
      winnie_dd_check_3_inspection: false,
      winnie_dd_check_4_ppsr: false,
      winnie_dd_check_5_price: false,
      winnie_dd_check_6_funds: false,
      hold_reason: reason.trim(),
      held_by_user_id: userId,
      held_at: new Date().toISOString(),
      held_at_stage: 'second_release',
      updated_at: new Date().toISOString(),
    })
    .eq('id', runId)
  if (error) throw error
}

/** Put a held run back into the queue it came from. */
export async function releaseHold(runId: string): Promise<void> {
  const { data, error: readError } = await supabase
    .from('payment_runs')
    .select('winnie_review_complete')
    .eq('id', runId)
    .single()
  if (readError) throw readError

  const { error } = await supabase
    .from('payment_runs')
    .update({
      status: data?.winnie_review_complete ? 'pending_tuo' : 'pending_winnie',
      hold_reason: null,
      held_by_user_id: null,
      held_at: null,
      held_at_stage: null,
      updated_at: new Date().toISOString(),
    })
    .eq('id', runId)
  if (error) throw error
}

export async function rejectRun(runId: string, reason: string, userId: string): Promise<void> {
  if (!reason.trim()) throw new Error('A reason is required to reject a payment run.')
  const { error } = await supabase
    .from('payment_runs')
    .update({
      status: 'rejected',
      rejected_reason: reason.trim(),
      rejected_by_user_id: userId,
      rejected_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    })
    .eq('id', runId)
  if (error) throw error
}

export const HOLD_STAGE_LABEL: Record<HoldStage, string> = {
  first_approval: 'First approval',
  second_release: 'Second release',
}
